// ---------------------------------------------------------------------------
// "The business day has ended."
//
// Shown when the drawer on screen is still open but its trading day has
// passed. It blocks selling and offers exactly one way forward: go and count
// the drawer.
//
// THREE THINGS IT DELIBERATELY DOES.
//
//   * It cannot be dismissed. No close button, no backdrop click, no Escape.
//     A cashier who could wave this away would carry on selling into
//     yesterday's takings - the server would refuse each attempt, but they
//     would find that out one refused customer at a time. The block IS the
//     explanation.
//
//   * It says the cart is safe, in words. The single most likely thing for a
//     cashier to fear when a wall appears mid-order is that they have lost the
//     basket they just built. They have not, and being told so is the
//     difference between following the instruction and force-quitting the app.
//
//   * It names the drawer and the day it belongs to. "Close the shift" is not
//     useful advice on its own when there are three shifts in the history and
//     the cashier does not know why today's selling suddenly stopped.
// ---------------------------------------------------------------------------

import { AlertTriangle, Clock, ShoppingBasket } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n/I18nProvider';
import { formatDate } from '../../utils/format';

interface Props {
  open: boolean;
  shiftNumber: string;
  /// `YYYY-MM-DD`, the trading day the drawer belongs to.
  businessDay: string;
  /// Whether this person is allowed to close the drawer. Somebody who is not
  /// still gets the block - they simply must not be told to do something the
  /// server will refuse.
  canClose: boolean;
}

export function BusinessDayEndedDialog({ open, shiftNumber, businessDay, canClose }: Props) {
  const { t, locale } = useI18n();

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/60 p-4 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="business-day-ended-title"
      aria-describedby="business-day-ended-body"
    >
      <div className="w-full max-w-md rounded-card border border-line-strong bg-surface p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-antique-gold/20 text-charcoal">
            <Clock className="h-6 w-6" aria-hidden="true" />
          </span>

          <div className="min-w-0">
            <h2 id="business-day-ended-title" className="text-lg font-semibold text-ink">
              {t.payment.dayEndedTitle}
            </h2>
            <p id="business-day-ended-body" className="mt-1.5 text-sm text-ink">
              {t.payment.dayEndedBody}
            </p>
          </div>
        </div>

        {/* Which drawer, and which day it holds. */}
        <p className="numeric mt-4 rounded-control bg-surface-sunken px-3 py-2 text-sm text-ink-muted">
          {t.payment.dayEndedShift
            .replace('{number}', shiftNumber)
            .replace('{day}', formatDate(businessDay, locale))}
        </p>

        <p className="mt-2 flex items-start gap-2 text-xs text-ink-muted">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {t.payment.dayEndedWhy}
        </p>

        {/* The reassurance. Nothing in the basket has been touched. */}
        <p className="mt-3 flex items-start gap-2 rounded-control bg-butcher-green/10 px-3 py-2 text-xs text-butcher-green">
          <ShoppingBasket className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {t.payment.dayEndedCart}
        </p>

        {canClose ? (
          <Link to="/shifts" className="btn-primary mt-5 w-full justify-center">
            {t.payment.dayEndedAction}
          </Link>
        ) : (
          // No button they cannot use. Being sent to a screen that refuses
          // them is worse than being told plainly to fetch somebody.
          <p className="mt-5 rounded-control border border-line-strong px-3 py-2 text-center text-xs text-ink-muted">
            {t.payment.shiftRequiredHint}
          </p>
        )}
      </div>
    </div>
  );
}
